import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/api/client";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Loader2, Gavel } from "lucide-react";

// Phase 4.5 Part 12 — Finance initiates the EXISTING approval workflow
// (services/approvals.py, same engine Phase 2/3 already built) from
// context. This never decides anything itself — it only creates a Pending
// approval that Management then works from the existing Approvals screen
// (Approve / Approve with Condition / Reject / Request More Information).
export function ExceptionDialog({ open, onOpenChange, decisionType, acceptanceId, objectType, objectId,
  defaultAmount, contextSummary, onDone }) {
  const [amount, setAmount] = useState("");
  const [justification, setJustification] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) { setAmount(defaultAmount ?? ""); setJustification(""); }
  }, [open, defaultAmount]);

  const submit = async () => {
    setSending(true);
    try {
      await api.post(`/finance-gate/acceptances/${acceptanceId}/request-exception`, {
        decisionType, requestedAmount: Number(amount), justification,
        objectType: objectType || "customer_acceptance", objectId: objectId || acceptanceId,
      });
      toast.success("Management exception requested — routed to the Approvals queue");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not request exception"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="exception-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Gavel className="h-4 w-4 text-violet-600" />Request Management exception</DialogTitle>
        </DialogHeader>
        {contextSummary && <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-600 whitespace-pre-line">{contextSummary}</div>}
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Amount involved</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              readOnly={defaultAmount != null} className={defaultAmount != null ? "bg-slate-50 text-slate-500" : ""}
              data-testid="exception-amount" />
            {defaultAmount != null && <div className="text-[11px] text-slate-400 mt-0.5">Set by the system to the exact amount Management must approve — an exception approved for a different figure will not be honored.</div>}
          </div>
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Reason / justification for Management</Label>
            <Textarea rows={4} value={justification} onChange={(e) => setJustification(e.target.value)}
              placeholder="Why should this proceed despite the exposure/credit/advance issue?" data-testid="exception-justification" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={sending || !justification.trim()} className="bg-violet-600 hover:bg-violet-700" data-testid="exception-submit">
            {sending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Send to Management
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
